import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiErr } from "@/lib/api";
import { fmtMoney, fmtDate, formatWebinarTime } from "@/lib/format";
import { toast } from "sonner";
import {
  LayoutDashboard, BookOpen, CalendarClock, Users, Receipt, Ticket,
  Bell, Settings as SettingsIcon, TrendingUp, GitBranch, Trash2,
} from "lucide-react";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: TrendingUp },
  { id: "funnel", label: "Funnel", icon: GitBranch },
  { id: "courses", label: "Courses", icon: BookOpen },
  { id: "webinars", label: "Webinars", icon: CalendarClock },
  { id: "registrations", label: "Registrations", icon: CalendarClock },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "students", label: "Students", icon: Users },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "notifications", label: "Notify Requests", icon: Bell },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <div className="asf-overline mb-2">Admin</div>
      <h1 className="font-display text-3xl font-bold tracking-tight">Control Center</h1>
      <div className="grid lg:grid-cols-[220px_1fr] gap-8 mt-6">
        <aside className="lg:sticky lg:top-24 h-max">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => setTab(n.id)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${tab === n.id ? "bg-asf-orange/15 text-asf-orange border border-asf-orange/30" : "text-slate-500 hover:bg-slate-100 border border-transparent"}`}>
                  <Icon className="h-4 w-4" /> {n.label}
                </button>
              );
            })}
          </nav>
        </aside>
        <div>
          {(tab === "dashboard" || tab === "analytics" || tab === "funnel") && <Analytics view={tab} />}
          {tab === "courses" && <AdminCourses />}
          {tab === "webinars" && <AdminWebinars />}
          {tab === "registrations" && <AdminRegistrations />}
          {tab === "orders" && <AdminOrders />}
          {tab === "students" && <AdminStudents />}
          {tab === "coupons" && <AdminCoupons />}
          {tab === "notifications" && <AdminNotify />}
          {tab === "settings" && <AdminSettings />}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 p-5">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-display text-3xl font-extrabold mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Analytics({ view }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.adminAnalytics().then(setA); }, []);
  if (!a) return <div className="text-slate-500">Loading…</div>;
  const money = (v) => `₹${Number(v).toLocaleString()}`;
  const FUNNEL = [
    ["Webinar registered", a.funnel["webinar.registered"]],
    ["Webinar attended", a.funnel["webinar.attended"]],
    ["Offer viewed", a.funnel["offer.viewed"]],
    ["Offer clicked", a.funnel["offer.clicked"]],
    ["Checkout started", a.funnel["checkout.started"]],
    ["Payment completed", a.funnel["payment.success"]],
    ["Course enrolled", a.funnel["course.enrolled"]],
    ["Course started", a.funnel["course.started"]],
    ["Course completed", a.funnel["course.completed"]],
  ];
  const max = Math.max(1, ...FUNNEL.map((f) => f[1]));

  if (view === "funnel") {
    return (
      <div data-testid="admin-funnel">
        <h2 className="font-display text-xl font-semibold mb-4">Conversion funnel</h2>
        <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6 space-y-3">
          {FUNNEL.map(([label, val]) => (
            <div key={label}>
              <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{label}</span><span className="font-mono text-asf-orange">{val}</span></div>
              <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-asf-orange" style={{ width: `${(val / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-xl font-semibold mb-4">Revenue</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card label="Today" value={money(a.revenue.today)} />
          <Card label="7 days" value={money(a.revenue.d7)} />
          <Card label="30 days" value={money(a.revenue.d30)} />
          <Card label="Total" value={money(a.revenue.total)} />
        </div>
      </div>
      <div>
        <h2 className="font-display text-xl font-semibold mb-4">Sales & webinars</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card label="Course sales" value={a.sales.courses} />
          <Card label="Bundle sales" value={a.sales.bundles} />
          <Card label="Registrations" value={a.webinars.registrations} />
          <Card label="Attendance" value={a.webinars.attendance} />
          <Card label="Offer eligible" value={a.webinars.eligible} />
          <Card label="Offer views" value={a.webinars.offer_views} />
          <Card label="Purchases" value={a.webinars.purchases} />
          <Card label="Conversion" value={`${a.webinars.conversion_rate}%`} />
        </div>
      </div>
      <div>
        <h2 className="font-display text-xl font-semibold mb-4">Courses</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card label="Enrollments" value={a.courses.enrollments} />
          <Card label="Completions" value={a.courses.completions} />
        </div>
      </div>
    </div>
  );
}

function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const load = () => api.adminCourses().then((d) => setCourses(d.courses));
  useEffect(() => { load(); }, []);
  const update = async (slug, patch) => {
    try { await api.adminUpdateCourse(slug, patch); toast.success("Updated"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto" data-testid="admin-courses">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200">
          <th className="p-3">Course</th><th className="p-3">₹ INR</th><th className="p-3">$ USD</th><th className="p-3">Enrolled</th><th className="p-3">Status</th>
        </tr></thead>
        <tbody>
          {courses.map((c) => (
            <tr key={c.slug} className="border-b border-slate-100">
              <td className="p-3">{c.title}</td>
              <td className="p-3"><input defaultValue={c.price_inr} onBlur={(e) => update(c.slug, { price_inr: Number(e.target.value) })} className="w-20 bg-asf-app border border-slate-200 rounded px-2 py-1" /></td>
              <td className="p-3"><input defaultValue={c.price_usd} onBlur={(e) => update(c.slug, { price_usd: Number(e.target.value) })} className="w-16 bg-asf-app border border-slate-200 rounded px-2 py-1" /></td>
              <td className="p-3">{c.enrollments}</td>
              <td className="p-3">
                <button onClick={() => update(c.slug, { published: !c.published })} className={`text-xs px-2.5 py-1 rounded-full ${c.published ? "bg-green-500/15 text-green-400" : "bg-slate-200 text-slate-500"}`}>
                  {c.published ? "Published" : "Draft"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminWebinars() {
  const { timezone } = useApp();
  const [topic, setTopic] = useState("ai");
  const [instances, setInstances] = useState([]);
  useEffect(() => { api.adminWebinarInstances(topic).then((d) => setInstances(d.instances)); }, [topic]);
  return (
    <div data-testid="admin-webinars">
      <div className="flex gap-2 mb-4">
        {["ai", "real-estate", "insurance", "loans"].map((t) => (
          <button key={t} onClick={() => setTopic(t)} className={`px-3 py-1.5 rounded-full text-sm ${topic === t ? "bg-asf-orange text-white" : "bg-slate-100 text-slate-600"}`}>{t}</button>
        ))}
      </div>
      <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-3">Session</th><th className="p-3">Local time</th><th className="p-3">Host</th><th className="p-3">Capacity</th><th className="p-3">Meeting URL</th></tr></thead>
          <tbody>
            {instances.map((w) => { const t = formatWebinarTime(w.start_utc, timezone); return (
              <tr key={w.instance_id} className="border-b border-slate-100">
                <td className="p-3">{w.title}</td>
                <td className="p-3">{t.dayLabel} {t.time} {t.tzShort}</td>
                <td className="p-3">{w.host}</td>
                <td className="p-3">{w.capacity}</td>
                <td className="p-3 text-asf-orange text-xs truncate max-w-[160px]">{w.meeting_url}</td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminRegistrations() {
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.adminRegistrations().then((d) => setRegs(d.registrations)); }, []);
  const exportCsv = () => {
    const rows = [["Name", "Email", "Mobile", "Country", "Topic", "Attended", "Date"],
      ...regs.map((r) => [r.name, r.email, r.mobile, r.country, r.topic, r.attended, r.created_at])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "registrations.csv"; a.click();
  };
  return (
    <div data-testid="admin-registrations">
      <div className="flex justify-between items-center mb-3">
        <h2 className="font-display text-xl font-semibold">Registrations ({regs.length})</h2>
        <button onClick={exportCsv} className="btn-secondary px-4 py-2 text-sm">Export CSV</button>
      </div>
      <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Topic</th><th className="p-3">Country</th><th className="p-3">Attended</th></tr></thead>
          <tbody>
            {regs.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="p-3">{r.name}</td><td className="p-3 text-slate-500">{r.email}</td>
                <td className="p-3">{r.topic}</td><td className="p-3">{r.country}</td>
                <td className="p-3">{r.attended ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.adminOrders().then((d) => setOrders(d.orders)); }, []);
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto" data-testid="admin-orders">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-3">Item</th><th className="p-3">Email</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Date</th></tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-slate-100">
              <td className="p-3">{o.title}</td><td className="p-3 text-slate-500">{o.email}</td>
              <td className="p-3 font-mono">{fmtMoney(o.symbol, o.amount)}</td>
              <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-500/15 text-green-400" : "bg-slate-200 text-slate-500"}`}>{o.payment_status}</span></td>
              <td className="p-3 text-slate-500">{fmtDate(o.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminStudents() {
  const [students, setStudents] = useState([]);
  useEffect(() => { api.adminStudents().then((d) => setStudents(d.students)); }, []);
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto" data-testid="admin-students">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Country</th><th className="p-3">Enrollments</th></tr></thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id} className="border-b border-slate-100"><td className="p-3">{s.name}</td><td className="p-3 text-slate-500">{s.email}</td><td className="p-3">{s.country}</td><td className="p-3">{s.enrollments}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: 10, scope: "all", target: "" });
  const load = () => api.adminCoupons().then((d) => setCoupons(d.coupons));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.adminCreateCoupon(form); toast.success("Coupon created"); setForm({ ...form, code: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="admin-coupons">
      <div className="asf-card !translate-y-0 hover:!translate-y-0 p-5 mb-4 grid sm:grid-cols-5 gap-2 items-end">
        <div><label className="text-xs text-slate-500">Code</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-full bg-asf-app border border-slate-200 rounded px-2 py-2 mt-1" /></div>
        <div><label className="text-xs text-slate-500">Type</label><select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full bg-asf-app border border-slate-200 rounded px-2 py-2 mt-1"><option value="percent">Percent</option><option value="fixed">Fixed</option></select></div>
        <div><label className="text-xs text-slate-500">Value {form.discount_type === "percent" ? "%" : "(INR)"}</label><input type="number" value={form.discount_type === "percent" ? form.value : form.value_inr || 0} onChange={(e) => setForm(form.discount_type === "percent" ? { ...form, value: Number(e.target.value) } : { ...form, value_inr: Number(e.target.value), value_usd: Math.round(Number(e.target.value) / 85) })} className="w-full bg-asf-app border border-slate-200 rounded px-2 py-2 mt-1" /></div>
        <div><label className="text-xs text-slate-500">Scope</label><select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="w-full bg-asf-app border border-slate-200 rounded px-2 py-2 mt-1"><option value="all">All</option><option value="industry">Industry</option><option value="course">Course</option><option value="bundle">Bundle</option></select></div>
        <button data-testid="create-coupon" onClick={create} className="btn-primary py-2.5">Create</button>
      </div>
      <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-3">Code</th><th className="p-3">Type</th><th className="p-3">Value</th><th className="p-3">Scope</th><th className="p-3">Used</th><th className="p-3"></th></tr></thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.code} className="border-b border-slate-100">
                <td className="p-3 font-mono text-asf-orange">{c.code}</td><td className="p-3">{c.discount_type}</td>
                <td className="p-3">{c.discount_type === "percent" ? `${c.value}%` : `₹${c.value_inr}`}</td>
                <td className="p-3">{c.scope}{c.target ? ` · ${c.target}` : ""}</td><td className="p-3">{c.used_count || 0}</td>
                <td className="p-3"><button onClick={() => api.adminDeleteCoupon(c.code).then(load)} className="text-red-400"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminNotify() {
  const [reqs, setReqs] = useState([]);
  useEffect(() => { api.adminNotifyRequests().then((d) => setReqs(d.requests)); }, []);
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto" data-testid="admin-notify">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Topic</th><th className="p-3">Country</th></tr></thead>
        <tbody>
          {reqs.map((r) => <tr key={r.id} className="border-b border-slate-100"><td className="p-3">{r.name}</td><td className="p-3 text-slate-500">{r.email}</td><td className="p-3">{r.topic}</td><td className="p-3">{r.country}</td></tr>)}
        </tbody>
      </table>
      {reqs.length === 0 && <p className="p-6 text-slate-500 text-sm">No notify-me requests yet.</p>}
    </div>
  );
}

const WEBHOOK_EVENTS = ["user.created", "webinar.registered", "webinar.attended", "offer.eligible", "offer.viewed", "checkout.started", "payment.success", "course.enrolled", "course.completed", "certificate.generated"];

function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.adminSettings().then(setCfg); }, []);
  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  const saveMode = async (mode) => { const d = await api.adminUpdateSettings({ eligibility_mode: mode }); setCfg(d); toast.success("Eligibility mode updated"); };
  const saveHook = async (event, url) => {
    const webhooks = { ...(cfg.webhooks || {}) };
    if (url) webhooks[event] = url; else delete webhooks[event];
    const d = await api.adminUpdateSettings({ webhooks }); setCfg(d); toast.success("Webhook saved");
  };
  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6">
        <h3 className="font-display text-lg font-semibold mb-3">Offer eligibility mode</h3>
        <div className="flex gap-2">
          {["registered", "attended", "manual"].map((m) => (
            <button key={m} data-testid={`eligibility-${m}`} onClick={() => saveMode(m)} className={`px-4 py-2 rounded-full text-sm capitalize ${cfg.eligibility_mode === m ? "bg-asf-orange text-white" : "bg-slate-100 text-slate-600"}`}>{m}</button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">Current: <strong>{cfg.eligibility_mode}</strong> — controls when a user becomes eligible for the webinar bundle offer.</p>
      </div>
      <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6">
        <h3 className="font-display text-lg font-semibold mb-3">Make.com / webhook endpoints</h3>
        <p className="text-xs text-slate-500 mb-4">Configure a URL per event. Events fire to these URLs (no credentials stored in code).</p>
        <div className="space-y-2">
          {WEBHOOK_EVENTS.map((ev) => (
            <div key={ev} className="flex gap-2 items-center">
              <span className="font-mono text-xs text-slate-500 w-48 shrink-0">{ev}</span>
              <input defaultValue={cfg.webhooks?.[ev] || ""} placeholder="https://hook.make.com/..."
                onBlur={(e) => saveHook(ev, e.target.value.trim())}
                className="flex-1 bg-asf-app border border-slate-200 rounded px-3 py-2 text-sm" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
